import { Box, Skeleton } from '@mui/material';
import PageContent from '@root/components/v2/layout/PageContent';
import React from 'react';

// Reusable skeleton card with configurable inner content
function SkeletonCard({ height = 300, children }) {
  return (
    <Box border="1px solid #e2e8f0" borderRadius="10px" height={height} padding="16px" overflow="hidden">
      <Skeleton width="45%" height={20} sx={{ mb: 1 }} />
      {children}
    </Box>
  );
}

// List-style skeleton (for Issues, Risky Accounts, Sensitive Data)
function ListSkeleton({ rows = 5 }) {
  return Array.from({ length: rows }, (_, i) => (
    <Box key={i} display="flex" alignItems="center" gap="10px" py="10px" borderTop={i > 0 ? '1px solid #edf0f4' : 'none'}>
      <Skeleton variant="rounded" width={31} height={31} sx={{ flexShrink: 0, borderRadius: '7px' }} />
      <Box flex={1}>
        <Skeleton width="70%" height={14} />
        <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  ));
}

// Table-style skeleton (for Vendors, Lateral Movement)
function TableSkeleton({ rows = 4 }) {
  return (
    <>
      <Box display="flex" gap="12px" py="8px" borderBottom="1px solid #edf0f4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} width={`${20 + i * 5}%`} height={14} />
        ))}
      </Box>
      {Array.from({ length: rows }, (_, i) => (
        <Box key={i} display="flex" gap="12px" py="12px" borderBottom="1px solid #edf0f4">
          {Array.from({ length: 4 }, (_, j) => (
            <Skeleton key={j} width={`${25 + j * 3}%`} height={14} />
          ))}
        </Box>
      ))}
    </>
  );
}

// Metric-style skeleton (for NHI Risk, Access Keys)
function MetricSkeleton({ rows = 4 }) {
  return Array.from({ length: rows }, (_, i) => (
    <Box key={i} display="flex" alignItems="center" gap="10px" py="8px" borderBottom={i < rows - 1 ? '1px solid #edf0f4' : 'none'}>
      <Skeleton variant="rounded" width={31} height={31} sx={{ flexShrink: 0, borderRadius: '7px' }} />
      <Box>
        <Skeleton width={60} height={26} />
        <Skeleton width={120} height={12} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  ));
}

// Map-style skeleton (for Country Activity card)
function MapSkeleton() {
  return (
    <>
      <Skeleton width="35%" height={14} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: '6px', mt: 1 }} />
      <Box display="flex" flexDirection="column" gap="4px" mt={2}>
        <Skeleton width={90} height={14} />
        {Array.from({ length: 3 }, (_, i) => (
          <Box key={i} display="flex" alignItems="center" gap="6px">
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton width={80 + i * 15} height={12} />
          </Box>
        ))}
      </Box>
    </>
  );
}

function NonHumanIdentitySkeleton() {
  return (
    <PageContent className="NonHumanIdentity">
      {/* ─── Tab strip: Overview / Findings ───────────────────────────── */}
      <Box display="flex" alignItems="center" gap="16px" height={40} px="12px">
        <Skeleton width={70} height={24} />
        <Skeleton width={70} height={24} />
      </Box>

      {/* ─── Top Row: 2 equal columns ─────────────────────────────────── */}
      <Box className="NonHumanIdentity__grid NonHumanIdentity__grid--top">
        <SkeletonCard>
          <Skeleton width="20%" height={14} sx={{ mb: 1 }} />
          <ListSkeleton rows={5} />
        </SkeletonCard>
        <SkeletonCard>
          <TableSkeleton rows={4} />
        </SkeletonCard>
      </Box>

      {/* ─── Middle Row: 3 columns (0.9fr / 0.9fr / 1.8fr) ───────────── */}
      <Box className="NonHumanIdentity__grid NonHumanIdentity__grid--middle">
        <SkeletonCard height={355}>
          <Skeleton width="25%" height={14} sx={{ mb: 1 }} />
          <ListSkeleton rows={5} />
        </SkeletonCard>
        <SkeletonCard height={355}>
          <MetricSkeleton rows={4} />
        </SkeletonCard>
        <SkeletonCard height={355}>
          <MapSkeleton />
        </SkeletonCard>
      </Box>

      {/* ─── Bottom Row: 3 columns (0.9fr / 0.9fr / 1.8fr) ──────────── */}
      <Box className="NonHumanIdentity__grid NonHumanIdentity__grid--bottom">
        <SkeletonCard>
          <ListSkeleton rows={4} />
        </SkeletonCard>
        <SkeletonCard>
          <MetricSkeleton rows={3} />
        </SkeletonCard>
        <SkeletonCard>
          <TableSkeleton rows={3} />
        </SkeletonCard>
      </Box>
    </PageContent>
  );
}

export default NonHumanIdentitySkeleton;
